import {Alert, Button, Card, Checkbox, Col, Divider, Radio, Row, Select, Space, Spin} from "antd";
import React, {useContext, useEffect, useState} from "react";
import {Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, SubTitle, Title, Tooltip,} from 'chart.js';
import {ConfigContext} from "./App";
import {LoadingOutlined} from "@ant-design/icons";
import {ALLOWED_COLOR_DARK, FAVORED_COLOR_DARK, OUTLIER_COLOR_DARK} from "./ColorUtil";
import Chart from "./Chart";
import {conformationSymbols, getModel, trim} from "./Util";
import {AMINO_ACID_ANGLES, ANGLE_ORDER_MAP, BACKBONE_ANGLES} from "./GeometryUtil";


ChartJS.register(LinearScale, PointElement, SubTitle, Title, LineElement, Tooltip, Legend);

const antIcon = <div><LoadingOutlined style={{fontSize: 60}} spin/><span>Loading...</span></div>;
const ALLOWED = "Allowed"
const OUTLIER = "Outlier"
const FAVORED = "Favoured"
const NONE = "None"
const ALL_AMINO_ACIDS = "ALL"

const getBulletPointColor = function (region){
    if (region === ALLOWED) {
        return ALLOWED_COLOR_DARK
    } else if (region === FAVORED) {
        return FAVORED_COLOR_DARK
    }
    return OUTLIER_COLOR_DARK
}

const getBackgroundImageUrl = function (enablePreproline, aminoAcid, model, selectedConformation) {
    if (aminoAcid === ALL_AMINO_ACIDS) {return null}
    if(!BACKBONE_ANGLES.includes(selectedConformation.x) && !AMINO_ACID_ANGLES[aminoAcid].rota.includes(selectedConformation.x)) return null;
    if(!BACKBONE_ANGLES.includes(selectedConformation.y) && !AMINO_ACID_ANGLES[aminoAcid].rota.includes(selectedConformation.y)) return null;

    if (!enablePreproline) {
        return `${process.env.PUBLIC_URL}/contour_plots/${model}/${aminoAcid}_${selectedConformation["x"]}_${selectedConformation["y"]}_contour_plot.png`
    }
    return `${process.env.PUBLIC_URL}/contour_plots/${model}/${aminoAcid}_${selectedConformation["x"]}_${selectedConformation["y"]}_prepro_contour_plot.png`
}

const getTitle = function (validationRegion, enablePreproline, outlierLowerBound, allowedLowerBound, aminoAcid, conformationName) {

    if (validationRegion === NONE) return `All ${aminoAcid === ALL_AMINO_ACIDS ? '': aminoAcid} ${conformationName} conformations`
    const preProlinePrefix = enablePreproline ? 'pre-proline' : ''
    if (validationRegion === ALLOWED) {
        return `${outlierLowerBound} bits ≤ ${preProlinePrefix} ${aminoAcid} ${conformationName} conformations < ${allowedLowerBound} bits`
    } else if (validationRegion === FAVORED) {
        return ` ${preProlinePrefix} ${aminoAcid} ${conformationName} conformations ≥ ${allowedLowerBound} bits`
    }
    return ` ${preProlinePrefix} ${aminoAcid} ${conformationName} conformations < ${outlierLowerBound} bits`
}

const compressionToValidationRegion = function (compression, outlierLowerBound, allowedLowerBound) {
    if (compression < outlierLowerBound) {
        return  OUTLIER
    } else if ((outlierLowerBound <= compression) && (compression < allowedLowerBound)) {
        return ALLOWED
    } else {
        return FAVORED
    }
}

const getFileName = function (pdb_id, aminoAcid, validationRegion, isPrePro, xName, yName) {

    const baseFileName = !isPrePro ? `phisical_checkup_${pdb_id}_${aminoAcid}` : `phisical_checkup_${pdb_id}_pre_pro_${aminoAcid}`
    if (validationRegion === NONE) return `${baseFileName}_${xName}_${yName}_contour_analysis`
    return `${baseFileName}_${validationRegion}_${xName}_${yName}_contour_analysis`
}

const getAminoAcidList = function (aggregatedData){
    const residueList  = aggregatedData[ALL_AMINO_ACIDS]?.map(data => {
        const labelValue = `${data.chain}:${data.aminoAcid}:${data.index}`
        return { data: data, value: labelValue}
    }) || []

    residueList.sort(function (l, r) {
        return l.data.dataIndex - r.data.dataIndex;
    });
    return residueList
}
function RamachandranAnalysis({pdb_id, data, model, dynamicColumns}) {

    const [aminoAcid, setAminoAcid] = useState(ALL_AMINO_ACIDS);
    const [validationRegion, setValidationRegion] = useState(NONE);
    const [isLoading, setLoading] = useState(false);
    const [isSecondaryImageLoading, setSecondaryPlotLoading] = useState(false);
    const {outlierLowerBound, allowedLowerBound, ..._} = useContext(ConfigContext);
    const [aggregatedData, setAggregatedData] = useState({});
    const [enablePreproline, setPreproline] = useState(false);
    const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
    const [secondaryBackgroundImageUrl, setSecondaryBackgroundImageUrl] = useState(null);
    const [aggregatedDataSecondary, setAggregatedDataSecondary] = useState({})
    const [isEntrySelected, setEntrySelected] =useState(false)
    const [entryOnContourPlot, showOnContourPlot] = useState({})
    const [selectedValue, setSelectedValue] = useState();
    const [selectedModel, setSelectedModel] = useState(model);
    const [fileName, setFileName] = useState(getFileName(pdb_id, ALL_AMINO_ACIDS, NONE, false))
    const [fileNameSpecial, setFileNameSpecial] = useState("")
    const [selectedConformation, setSelectedConformation] = useState({})
    const [conformationList, setConformationList] = useState([])
    const [chartTitle, setChartTitle] = useState('');

    useEffect(() => {
        const aggregatedDataTemp = data.reduce((acc, cur, i) => {
            const currentAminoAcidData = acc[cur['amino_acid']] || []

            const compressionData = cur[selectedConformation?.key]
            if (compressionData) {
                let isCompressionInValidationRegion = true
                const compression = parseFloat(compressionData.compression)
                const validationRegionOfEntry = compressionToValidationRegion(compression, outlierLowerBound, allowedLowerBound)
                if (validationRegion !== NONE) {
                    isCompressionInValidationRegion = validationRegionOfEntry === validationRegion
                }
                if (isCompressionInValidationRegion && cur["isPreProline"] === enablePreproline && cur["mergeColumns"] !== 0) {
                    let aminoAcidName =  cur['amino_acid']
                    if (cur["isCisConformation"] && aminoAcidName !== "cis-PRO") {
                        aminoAcidName = cur['amino_acid'].split("-")[1]
                    }

                    return {
                        ...acc,
                        [cur['amino_acid']]: currentAminoAcidData.concat({
                            compression: compressionData.compression,
                            aminoAcid: aminoAcidName,
                            chain: cur['chain'],
                            index: cur["index"],
                            x: trim(cur['angles'][selectedConformation.x]),
                            y: trim(cur['angles'][selectedConformation.y]),
                            z: trim(cur['angles'][selectedConformation.z]),
                            dataIndex: i,
                            isConditioned: false,
                            color:getBulletPointColor(validationRegionOfEntry),
                            model:getModel(cur['model']).modelText
                        })
                    }
                }
            }
            return acc
        }, {})
        const aggregatedDataAll = {
            ...aggregatedDataTemp,
            [`${ALL_AMINO_ACIDS}`] : [].concat(...Object.values(aggregatedDataTemp))
        }
        setAggregatedData(aggregatedDataAll)
        setAggregatedDataSecondary(aggregatedDataAll)
        setSelectedModel(model)
    }, [validationRegion, allowedLowerBound, outlierLowerBound, data, enablePreproline, model, selectedConformation])

   useEffect(() => {
        const filteredConformations = dynamicColumns.marginal.filter(element => element.name === "rama" ||
            ((element.name.split(",").length) === 2 && (!element.name.includes("rama") || !element.name.includes("rota") || !element.name.includes("backbone"))))

        const conformationInfo = filteredConformations.map(element => {
            let angleOrder = []
            if (element.name === "rama") {
                angleOrder.push({
                    name: "phi", value: ANGLE_ORDER_MAP["phi"], symbol: "φ"
                })
                angleOrder.push({name: "psi", value: ANGLE_ORDER_MAP["psi"], symbol: "ψ"})
            } else {
                angleOrder = element.key.split(",").map((angleName, i) => {
                    return {name: angleName, value: ANGLE_ORDER_MAP[angleName], symbol: conformationSymbols[angleName]}
                })
            }
            angleOrder.sort(function (a, b) {
                return a.value - b.value;
            });
            return {
                value: angleOrder[0].name + "," + angleOrder[1].name,
                label: angleOrder[0].symbol + "," + angleOrder[1].symbol,
                key:element.key,
                x:angleOrder[0].name,
                y:angleOrder[1].name,
                x_symbol:angleOrder[0].symbol,
                y_symbol:angleOrder[1].symbol,
                z:"omega"
            }
        })
       const conformationNameList = conformationInfo.map(e => e.value)

       const uniqueConformationList = conformationInfo.filter(function(item, pos) {
           return conformationNameList.indexOf(item.value) === pos;
       })
       setSelectedConformation(uniqueConformationList[0])
       setConformationList(uniqueConformationList)
       setChartTitle(`All ${uniqueConformationList[0].label} conformations`)
    }, [dynamicColumns])


    return (
        <Card style={{overflowX:"auto", paddingLeft:10, paddingRight:10, scrollbarWidth:'thin'}}>
            <div style={{minWidth: "1450px"}}>
                <Spin indicator={antIcon} spinning={isLoading || isSecondaryImageLoading} style={{top: '20%'}}>
                    {((data.length > 0 && conformationList.length > 0) ) ?
                        <>
                            <Row>
                                <Col span={3} style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                                    Select a Conformation:
                                </Col>
                                <Col span={5} style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                                    <Select style={{width: 100}}
                                            options={conformationList}
                                            defaultValue={selectedConformation}
                                            onChange={(value,valueObject )=>{
                                                setSelectedConformation(valueObject)
                                                setBackgroundImageUrl(getBackgroundImageUrl(enablePreproline, aminoAcid, selectedModel, valueObject))
                                                setChartTitle(getTitle(validationRegion, enablePreproline, outlierLowerBound, allowedLowerBound, aminoAcid, valueObject.label))
                                                setFileName(getFileName(pdb_id, aminoAcid, validationRegion, enablePreproline, valueObject["x"], valueObject["y"]))
                                            }}
                                    />
                                </Col>
                            </Row>
                            <Row style={{display: 'flex', paddingTop: '3vh'}}>
                                <Col span={3} style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                                    Select validation region:
                                </Col>
                                <Col style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start'}}>
                                    <Radio.Group onChange={({target}) => {
                                        setValidationRegion(target.value)
                                        setChartTitle(getTitle(target.value, enablePreproline, outlierLowerBound, allowedLowerBound, aminoAcid, selectedConformation.label))
                                        setSelectedValue([])
                                        setEntrySelected(false)
                                        setFileName(getFileName(pdb_id, aminoAcid, target.value, enablePreproline, selectedConformation.x, selectedConformation.y))
                                    }
                                    } value={validationRegion}>
                                        <Radio value={NONE} >{NONE}</Radio>
                                        <Radio value={OUTLIER}  >{OUTLIER}</Radio>
                                        <Radio value={ALLOWED} >{ALLOWED}</Radio>
                                        <Radio value={FAVORED}  >{FAVORED}</Radio>
                                    </Radio.Group>
                                </Col>
                            </Row>
                            <Divider/>
                            <Row style={{display: 'flex'}}>
                                <Col span={3} style={{display: 'flex',textAlign:'justify', flexDirection: 'column', alignItems: 'flex-start', gap:40}}>
                                    Show Amino Acids  <br/> on contour plot:
                                    <Checkbox onChange={(e) => {
                                        setLoading(true)
                                        setPreproline(e.target.checked)
                                        setBackgroundImageUrl(getBackgroundImageUrl(e.target.checked, aminoAcid, selectedModel, selectedConformation))
                                        setChartTitle(getTitle(validationRegion, e.target.checked, outlierLowerBound, allowedLowerBound, aminoAcid, selectedConformation.label))
                                        setSelectedValue([])
                                        setEntrySelected(false)
                                        setFileName(getFileName(pdb_id, aminoAcid, validationRegion, e.target.checked, selectedConformation.x, selectedConformation.y))
                                    }} checked={enablePreproline}>pre-proline </Checkbox>
                                </Col>
                                <Col span={20} style={{display: 'flex', flexDirection: 'column', alignItems:'flex-start'}}>
                                    <Radio.Group onChange={({target}) => {
                                        setLoading(true)
                                        setAminoAcid(ALL_AMINO_ACIDS)
                                        setChartTitle(getTitle(validationRegion, enablePreproline, outlierLowerBound, allowedLowerBound, ALL_AMINO_ACIDS, selectedConformation.label))
                                        setBackgroundImageUrl(null)
                                        setFileName(getFileName(pdb_id, ALL_AMINO_ACIDS, validationRegion, enablePreproline, selectedConformation.x, selectedConformation.y))
                                    }} value={aminoAcid}>
                                        <Radio value={ALL_AMINO_ACIDS}>ALL</Radio>
                                    </Radio.Group>
                                    <Divider/>
                                    <Radio.Group onChange={({target}) => {
                                        const tempAminoAcid = target.value
                                        setLoading(true)
                                        setAminoAcid(tempAminoAcid)
                                        setBackgroundImageUrl(getBackgroundImageUrl(enablePreproline, tempAminoAcid, selectedModel, selectedConformation))
                                        setChartTitle(getTitle(validationRegion, enablePreproline, outlierLowerBound, allowedLowerBound, tempAminoAcid, selectedConformation.label))
                                        setAggregatedData(aggregatedDataSecondary)
                                        setSelectedValue([])
                                        setEntrySelected(false)
                                        setFileName(getFileName(pdb_id, tempAminoAcid, validationRegion, enablePreproline, selectedConformation.x, selectedConformation.y))
                                    }} value={aminoAcid}>
                                        <Space direction="vertical"  align="start">
                                            <Radio value={"ALA"}>ALA</Radio>
                                            <Radio value={"TYR"}>TYR</Radio>
                                            <Radio value={"cis-PRO"}>cis_PRO</Radio>
                                        </Space>
                                        <Space direction="vertical" >
                                            <Radio value={"GLY"}>GLY</Radio>
                                            <Radio value={"ASP"}>ASP</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"CYS"}>CYS</Radio>
                                            <Radio value={"HIS"}>HIS</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"SER"}>SER</Radio>
                                            <Radio value={"ASN"}>ASN</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"VAL"}>VAL</Radio>
                                            <Radio value={"PRO"}>PRO</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"THR"}>THR</Radio>
                                            <Radio value={"GLN"}>GLN</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"ILE"}>ILE</Radio>
                                            <Radio value={"GLU"}>GLU</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"LEU"}>LEU</Radio>
                                            <Radio value={"MET"}>MET</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"PHE"}>PHE</Radio>
                                            <Radio value={"LYS"}>LYS</Radio>
                                        </Space>
                                        <Space direction="vertical">
                                            <Radio value={"TRP"}>TRP</Radio>
                                            <Radio value={"ARG"}>ARG</Radio>
                                        </Space>
                                    </Radio.Group>

                                </Col>
                            </Row>
                            {aminoAcid === ALL_AMINO_ACIDS && validationRegion === NONE && (
                                <>
                                    <Divider/>
                                    <Row style={{display: 'flex'}}>
                                        <Col span={3} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                        }}>
                                            Filter residues:
                                        </Col>
                                        <Col span={5} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',

                                        }}>
                                            <Space direction="vertical" style={{width: '100%'}}>
                                                <Select mode='multiple' style={{width: '100%'}} options=
                                                    {getAminoAcidList(aggregatedDataSecondary)}
                                                        value={selectedValue}
                                                        placeholder='chain:residue name:index'
                                                        maxTagCount='responsive' onChange={(value, dataList) => {
                                                    if (value.length) {
                                                        setSelectedValue(value)
                                                        const tempFilteredData = {ALL: dataList.map(dataEntry => (dataEntry.data))}
                                                        setAggregatedData(tempFilteredData)
                                                        setEntrySelected(false)
                                                        setChartTitle(`${selectedConformation.label} conformations of selected residues`)
                                                        setFileName(getFileName(pdb_id, 'filtered', validationRegion, enablePreproline, selectedConformation.x, selectedConformation.y))
                                                    }
                                                }
                                                } labelInValue={true} allowClear={true} onClear={()=>{
                                                    setAggregatedData(aggregatedDataSecondary)
                                                    setSelectedValue([])
                                                    setEntrySelected(false)
                                                    setChartTitle(`All ${selectedConformation.label} conformations`)
                                                }}/>
                                            </Space>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Divider style={{paddingTop: '1vh', paddingBottom:'1vh'}}> </Divider>

                            <Row justify={'center'} style={{display:'flex', alignItems:'flex-end'}}>
                                <Col span={12} >
                                    <Chart
                                        key={'default-chart'}
                                        chartProps={{
                                            heading:chartTitle,
                                            subHeading:`# of conformations: ${aggregatedData[aminoAcid]?.length || 0}`,
                                            chartLength: 624,
                                            bulletSize: 15,
                                            min: -180,
                                            max: 180,
                                            stepSize: 30,
                                            borderColor: 'grey',
                                            xAxis:selectedConformation.x_symbol,
                                            yAxis:selectedConformation.y_symbol,
                                            popupContentRender:(popupContent)=>
                                                <div >
                                                    <h4>{popupContent.data.chain}:{popupContent.data.aminoAcid}:{popupContent.data.index}</h4>
                                                    {((aminoAcid !== ALL_AMINO_ACIDS &&
                                                            !((aminoAcid === "PRO" && !popupContent.data.isConditioned))) || ((aminoAcid === "PRO" && !popupContent.data.isConditioned))) &&
                                                        <div>
                                                            <p>{selectedConformation.x_symbol},{selectedConformation.y_symbol}  =({popupContent.data.x}º, {popupContent.data.y}º), compression = {popupContent.data.compression} bits</p>
                                                            <p>model  = {popupContent.data.model}</p>
                                                        </div>
                                                    }
                                                    {(( (aminoAcid === "PRO" || aminoAcid === "cis-PRO") && selectedConformation.value === "phi,psi" && popupContent.data.z && !popupContent.data.isConditioned))
                                                        && <Button type="primary" shape="round" size="small"
                                                                   onClick={()=>{
                                                                       setSecondaryPlotLoading(true)
                                                                       setEntrySelected(true)
                                                                       const conditionDataEntry = data[popupContent.data.dataIndex + 1]
                                                                       const compressionData = conditionDataEntry['rama'] || conditionDataEntry["phi,psi"] || conditionDataEntry["psi,phi"]
                                                                       const conditionedCompression = parseFloat(compressionData.compression)
                                                                       setFileNameSpecial(getFileName(pdb_id, `${popupContent.data.chain}_PRO_${popupContent.data.index}_conditioned`, validationRegion, enablePreproline, selectedConformation.x, selectedConformation.y))
                                                                       showOnContourPlot({[aminoAcid]:[{...popupContent.data,
                                                                               compression: conditionedCompression,
                                                                               dataIndex: popupContent.data.dataIndex + 1,
                                                                               isConditioned: true,
                                                                               model:'conditioned \n on ω'
                                                                           }]})
                                                                       const omega = Math.round(popupContent.data.z)
                                                                       setSecondaryBackgroundImageUrl(`${process.env.PUBLIC_URL}/contour_plots/${selectedModel}/PRO_phi_psi_${omega}_contour_plot.png`)

                                                                   }}> Show on φ,ψ|ω={Math.round(popupContent.data.z)}º </Button>
                                                    }
                                                    {aminoAcid === ALL_AMINO_ACIDS &&
                                                        <Button type="primary" shape="round" size="small"
                                                                onClick={() => {
                                                                    setSecondaryPlotLoading(true)
                                                                    setEntrySelected(true)
                                                                    const selectedAminoAcid = popupContent.data.aminoAcid
                                                                    showOnContourPlot({"ALL": [popupContent.data]})
                                                                    setFileNameSpecial(getFileName(pdb_id, `${popupContent.data.chain}_${selectedAminoAcid}_${popupContent.data.index}`, validationRegion, enablePreproline, selectedConformation.x, selectedConformation.y))
                                                                    setSecondaryBackgroundImageUrl(getBackgroundImageUrl(enablePreproline, selectedAminoAcid, selectedModel, selectedConformation))
                                                                }}> Show on contour plot </Button>
                                                    }
                                                </div>,
                                            backgroundImageUrl: backgroundImageUrl,
                                            fileName: fileName,
                                            scatterData:aggregatedData[aminoAcid]?.map((data) => ({x: data.x, y: data.y, popupContent:{data}, popupTitle: `Residue: ${data.chain}:${data.aminoAcid}:${data.index}`, color: data.color})) || [],
                                            onCanvasLoaded:()=>{setLoading(false)},
                                            deselectEntry:() => {setEntrySelected(false)},
                                        }}/>
                                </Col>
                                <Col span={12}>
                                    {isEntrySelected && ((aminoAcid === ALL_AMINO_ACIDS) || (aminoAcid==="PRO" || (aminoAcid==="cis-PRO"))) && <Chart
                                        key={'special-chart'}
                                        chartProps={{
                                            heading:'',
                                            subHeading: '',
                                            chartLength: 624,
                                            bulletSize: 15,
                                            min: -180,
                                            max: 180,
                                            stepSize: 30,
                                            borderColor: 'grey',
                                            xAxis:selectedConformation.x_symbol,
                                            yAxis:selectedConformation.y_symbol,
                                            popupContentRender:(popupContent)=> <div>
                                                    <h4>{popupContent.data.chain}:{popupContent.data.aminoAcid}:{popupContent.data.index}</h4>
                                                    <p>{selectedConformation.x_symbol},{selectedConformation.y_symbol}  =({popupContent.data.x}º, {popupContent.data.y}º), compression = {popupContent.data.compression} bits</p>
                                                    <p>model  = {popupContent.data.model}</p>
                                                </div>,
                                            backgroundImageUrl: secondaryBackgroundImageUrl,
                                            fileName: fileNameSpecial,
                                            scatterData:entryOnContourPlot[aminoAcid]?.map((data) => ({x: data.x, y: data.y, popupContent:{data}, popupTitle: `Residue: ${data.aminoAcid}:${data.chain}:${data.index}`, color: data.color})) || [],
                                            onCanvasLoaded:()=>{setSecondaryPlotLoading(false)},
                                        }}/>}
                                </Col>
                            </Row>
                        </> :
                        <>
                            <Alert
                                style={{width:'fit-content', textAlign:"left"}}
                                message="No validation information found"
                                description="Please submit a PDB ID or upload a PDB file"
                                type="info"
                                showIcon
                            />
                        </>}
                </Spin>
            </div>
        </Card>
    )
}



export default RamachandranAnalysis;